import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, Link, Stack, Typography } from '@mui/material';
import CopyableText from 'components/atoms/CopyableText';
import { JobArchiveControl } from 'components/Pages/Jobs/JobArchiveControl';
import { getJobStatusDisplayLabel, JobStatusChip } from 'components/Pages/Jobs/JobStatusChip';
import { DuplicateIngest } from 'interfaces/fileList';
import { filter, size } from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { formatDateTimeForUser } from 'utils/dateTime';

interface DuplicateIngestsCellProps {
  duplicateIngests: DuplicateIngest[];
  rowLabel: string;
  getJobUrl: (jobId: string, archived: boolean) => string | null;
}

const getIngestDate = ({ ingestJob, slideCreatedAt }: DuplicateIngest) => {
  const date = ingestJob?.startedAt || slideCreatedAt;
  return date ? formatDateTimeForUser(date) : 'Start date unavailable';
};

const DuplicateIngestItem = ({
  ingest,
  rowLabel,
  jobUrl,
}: {
  ingest: DuplicateIngest;
  rowLabel: string;
  jobUrl: string | null;
}) => {
  const { ingestJob, slideId, cases, isArchived, isCurrent, hasFurtherJobs, suggestedForArchive } = ingest;
  const ingestDate = getIngestDate(ingest);
  const statusChip = ingestJob ? (
    <JobStatusChip
      status={ingestJob.status || undefined}
      label={getJobStatusDisplayLabel(ingestJob.status || undefined)}
    />
  ) : null;

  return (
    <Box
      component="li"
      aria-label={`Ingest of ${rowLabel} from ${ingestDate}`}
      sx={{ listStyle: 'none', py: 0.5, '&:not(:first-of-type)': { borderTop: 1, borderColor: 'divider' } }}
    >
      <Stack spacing={0.25}>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary">
            {ingestDate}
          </Typography>
          {jobUrl && statusChip ? (
            <Link
              component={RouterLink}
              to={jobUrl}
              underline="none"
              aria-label={`Open ingest job from ${ingestDate}`}
              sx={{ display: 'inline-flex' }}
            >
              {statusChip}
            </Link>
          ) : (
            statusChip
          )}
          {isCurrent ? <Chip size="small" variant="outlined" label="Current" /> : null}
          {isArchived ? <Chip size="small" variant="outlined" label="Archived" /> : null}
          {suggestedForArchive ? <Chip size="small" color="warning" label="Suggested to archive" /> : null}
        </Stack>
        {ingestJob && ingestJob.status !== 'completed' && ingestJob.statusMessage ? (
          <Typography variant="caption" color="text.secondary" noWrap title={ingestJob.statusMessage}>
            {ingestJob.statusMessage}
          </Typography>
        ) : null}
        {slideId ? (
          <CopyableText
            variant="caption"
            textForCopy={slideId}
            tooltipText="Copy slide ID"
            copyWithButton="right"
            copyButtonVisibility="always"
            copyButtonProps={{ size: 'small', 'aria-label': `Copy slide ID ${slideId}` }}
            copyIconProps={{ fontSize: 'small' }}
            sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
          >
            {slideId}
          </CopyableText>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No slide created
          </Typography>
        )}
        {slideId ? (
          <Typography variant="caption" color="text.secondary">
            {cases.length
              ? `Assigned to ${cases.map(({ displayName }) => displayName).join(', ')}`
              : 'Not assigned to a case'}
            {hasFurtherJobs ? ' · Has further jobs' : ''}
          </Typography>
        ) : null}
        {ingestJob ? (
          <Box>
            <JobArchiveControl
              labeled
              confirmationNotes={{
                archive: 'The slide created by this ingest will no longer appear in lost and found.',
                restore: 'The slide created by this ingest will return to lost and found if it has no case.',
              }}
              job={{
                id: ingestJob.id,
                name: `ingest of ${rowLabel} from ${ingestDate}`,
                status: ingestJob.status || undefined,
                userId: ingestJob.userId || undefined,
                deletedAt: ingestJob.archivedAt || undefined,
              }}
            />
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};

/**
 * Lists every ingest of one upload. QMS rule: duplicates and archived ingests stay visible by default;
 * collapsing is an explicit, per-row user choice.
 */
const DuplicateIngestsCell = ({ duplicateIngests, rowLabel, getJobUrl }: DuplicateIngestsCellProps) => {
  const [isExpanded, setIsExpanded] = React.useState(true);

  if (!size(duplicateIngests)) {
    return null;
  }

  const archivedCount = size(filter(duplicateIngests, 'isArchived'));
  const summary = `${duplicateIngests.length} ingests of this upload${
    archivedCount ? ` · ${archivedCount} archived` : ''
  }`;

  return (
    <Stack spacing={0.25} sx={{ minWidth: 0, width: '100%' }}>
      <Button
        size="small"
        color="warning"
        variant="contained"
        aria-expanded={isExpanded}
        aria-label={`${summary}. ${isExpanded ? 'Hide' : 'Show'} ingests of ${rowLabel}`}
        endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={() => setIsExpanded((current) => !current)}
        sx={{ alignSelf: 'flex-start' }}
      >
        {summary}
      </Button>
      {isExpanded ? (
        <Box component="ul" aria-label={`Ingests of ${rowLabel}`} sx={{ m: 0, p: 0 }}>
          {duplicateIngests.map((ingest) => (
            <DuplicateIngestItem
              key={`${ingest.ingestJob?.id ?? 'no-job'}:${ingest.slideId ?? 'no-slide'}`}
              ingest={ingest}
              rowLabel={rowLabel}
              jobUrl={ingest.ingestJob ? getJobUrl(ingest.ingestJob.id, ingest.isArchived) : null}
            />
          ))}
        </Box>
      ) : null}
    </Stack>
  );
};

export default DuplicateIngestsCell;
